import React, { useState } from 'react';
import { useUser } from 'contexts';
import { Lock, Trash2, X, AlertCircle, Eye, EyeOff } from 'lucide-react';

const MultiDeletePasswordModal = ({ isOpen, onClose, onConfirm }) => {
   const { user } = useUser();
   const [password, setPassword] = useState('');
   const [error, setError] = useState('');
   const [isValidating, setIsValidating] = useState(false);
   const [showPassword, setShowPassword] = useState(false);

   const handleSubmit = () => {
      setIsValidating(true);
      setError('');

      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const correctPassword = `${month}${day}`;

      if (password === correctPassword) {
         setTimeout(() => {
            onConfirm();
            setPassword('');
            setError('');
            setIsValidating(false);
            onClose();
         }, 500);
      } else {
         setError('Incorrect password. Please enter valid password.');
         setIsValidating(false);
      }
   };

   const handleClose = () => {
      setPassword('');
      setError('');
      setIsValidating(false);
      onClose();
   };

   const handleKeyPress = (e) => {
      if (e.key === 'Enter' && password.length === 4) {
         handleSubmit();
      }
   };

   if (!isOpen) return null;

   return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
         <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-red-500 to-rose-600 rounded-t-2xl p-6">
               <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-all"
               >
                  <X className="w-5 h-5" />
               </button>
               <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-full p-3">
                     <Trash2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                     <h2 className="text-2xl font-bold text-white">Delete Confirmation</h2>
                     <p className="text-red-100 text-sm">This action cannot be undone</p>
                  </div>
               </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
               <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                     You must enter a valid password to continue the deletion process.
                  </p>
               </div>

               <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                     Enter Password
                  </label>
                  <div className="relative">
                     <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                     </div>
                     <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                           setPassword(e.target.value);
                           setError('');
                        }}
                        onKeyPress={handleKeyPress}
                        placeholder="Password"
                        maxLength="4"
                        className={`block w-full pl-10 pr-3 py-3 border ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                           } rounded-lg focus:outline-none focus:ring-2 text-lg tracking-wider`}
                        autoComplete="off"
                     />

                     {/* Eye Toggle */}
                     <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                     >
                        {showPassword ? (
                           <EyeOff className="h-5 w-5" />
                        ) : (
                           <Eye className="h-5 w-5" />
                        )}
                     </button>
                  </div>
                  {error && (
                     <div className="flex items-center space-x-2 text-red-600 text-sm mt-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>{error}</span>
                     </div>
                  )}
               </div>

               {/* Buttons */}
               <div className="flex space-x-3 pt-2">
                  <button
                     type="button"
                     onClick={handleClose}
                     className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                  >
                     Cancel
                  </button>
                  <button
                     type="button"
                     onClick={handleSubmit}
                     disabled={isValidating || password.length !== 4}
                     className={`flex-1 px-4 py-3 rounded-lg font-medium text-white transition-all focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center space-x-2 ${isValidating || password.length !== 4
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-lg'
                        }`}
                  >
                     {isValidating ? (
                        <>
                           <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                           <span>Verifying...</span>
                        </>
                     ) : (
                        <>
                           <Trash2 className="w-5 h-5" />
                           <span>Delete Record</span>
                        </>
                     )}
                  </button>
               </div>
            </div>
         </div>
      </div>
   );
};

export default MultiDeletePasswordModal;
